import React from "react";
import { Link } from "react-router-dom";

const legalUpdated = process.env.REACT_APP_LEGAL_LAST_UPDATED?.trim();
const publicEmail = process.env.REACT_APP_CONTACT_EMAIL?.trim();

export default function PrivacyPolicy() {
  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
      <p className="text-sm text-muted-foreground mb-8">{legalUpdated ? `Last updated: ${legalUpdated}` : "Publication date pending legal approval"}</p>

      <div className="prose prose-lg max-w-none space-y-6 text-muted-foreground">
        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">1. Information We Collect</h2>
          <p>We collect information you provide directly:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Account information (name, email, phone number)</li>
            <li>Property listing details (for agents/offices)</li>
            <li>Search preferences and saved properties</li>
            <li>Appointment booking details</li>
            <li>Communications through our platform</li>
          </ul>
          <p className="mt-3">Automatically collected information:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Device information and browser type</li>
            <li>Technical request data processed by the hosting and security layers</li>
            <li>Supported property interaction events while signed in</li>
            <li>Essential authentication and CSRF cookies</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">2. How We Use Your Information</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>Provide and improve our property search services</li>
            <li>Return property recommendations from the preferences you submit</li>
            <li>Facilitate agent-buyer communication</li>
            <li>Process appointment bookings</li>
            <li>Send relevant notifications and updates</li>
            <li>Prevent fraud and ensure platform security</li>
            <li>Generate operational analytics from supported property interactions</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">3. Data Protection</h2>
          <p>The application includes security controls such as:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>HTTPS-dependent secure, httpOnly authentication cookies in production</li>
            <li>CSRF validation and role/record-level access checks</li>
            <li>Login lockout and limits on selected sensitive operations</li>
            <li>Narrow public property and professional-profile responses</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">4. Information Sharing</h2>
          <p>Information may be processed or disclosed where needed to operate the service, including:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Assigned agents or offices involved in an enquiry or site visit</li>
            <li>Service providers who assist our operations (hosting, analytics)</li>
            <li>Law enforcement when required by law</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">5. Your Rights</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>Update supported profile information from your account</li>
            <li>Ask questions or submit a privacy request through the contact channel</li>
            <li>Exercise additional rights available under applicable law</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">6. Cookies</h2>
          <p>
            The application uses essential cookies for authentication and request protection.
            Third-party sign-in, fonts, maps, or tiles may be subject to their providers' policies.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">7. Data Retention</h2>
          <p>
            Retention depends on account, operational, security, transaction, and legal requirements.
            Contact the platform operator for the policy that applies to your record.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-3">8. Contact</h2>
          <p>
            For privacy-related queries, {publicEmail ? <a href={`mailto:${publicEmail}`} className="text-primary hover:underline">contact {publicEmail}</a> : <Link to="/contact" className="text-primary hover:underline">use the contact form</Link>}.
          </p>
        </section>
      </div>
    </div>
  );
}
